import { connect as formikConnect, ErrorMessage } from 'formik';
import values from 'lodash/values';
import PropTypes from 'prop-types';
import React from 'react';

import { createBEM } from '../../../lib/utils/css';
import ButtonT3 from '../ButtonT3/ButtonT3';
import { ButtonT3Size, ButtonT3Theme } from '../ButtonT3/constants';

import { FormItemTypes, FormThemes } from './constants';
import FormAutocomplete from './FormAutocomplete/FormAutocomplete';
import FormInput from './FormInput/FormInput';
import FormRadioButtonGroup from './FormRadioButtonGroup/FormRadioButtonGroup';
import FormSelect from './FormSelect/FormSelect';
import * as FormUtils from './utils';

import './FormItem.scss';

/**
 * @param  {string} theme - Is fed by parent Form
 * @param  {string} type - FormInput | FormSelect | FormRadioButtonGroup
 * @param  {string} label - Text on top of the input
 * @param  {string} ariaLabel - Text for aria-label
 * @param  {string} description - Hidden description for screen reader
 * @param  {string} hint - Text below input
 * @param  {object} inputAttributes - Defines attributes for FormItem. (FormInput | FormSelect | FormRadioButtonGroup) Validation occurs in respective FormPartial
 * @param  {object} explanationControl - Provides an informational trigger set up
 * @param  {object} showsOn - only appears if certain parameters are met
 * @param  {Any} formik - Provided by formik connect
 */

class FormItem extends React.Component {
  static propTypes = {
    theme: PropTypes.string,
    type: PropTypes.oneOf(values(FormItemTypes)),
    label: PropTypes.node,
    ariaLabel: PropTypes.string,
    description: PropTypes.string,
    hint: PropTypes.string,
    inputAttributes: PropTypes.object,
    explanationControl: PropTypes.shape({
      onClick: PropTypes.func,
      text: PropTypes.string,
      helperText: PropTypes.string,
      qaTag: PropTypes.string,
    }),
    showsOn: PropTypes.object,
    formik: PropTypes.object,
  };

  renderExplanationControl = () => {
    const { text, helperText, onClick, qaTag } = this.props.explanationControl;
    const explanationButtonTheme =
      this.props.theme === FormThemes.DARK_BACKGROUND
        ? ButtonT3Theme.DARK_TRANSPARENT
        : ButtonT3Theme.LIGHT_TRANSPARENT;
    return (
      <div className='FormItem__explanation'>
        <ButtonT3
          theme={explanationButtonTheme}
          onClick={onClick}
          qaTag={qaTag}
          size={ButtonT3Size.SMALL}
          isNoPadding={true}
        >
          {text}
        </ButtonT3>
        <span className='a11y--visuallyHidden'>{helperText}</span>
      </div>
    );
  };

  renderItem = () => {
    switch (this.props.type) {
      case FormItemTypes.SELECT:
        return <FormSelect {...this.props} />;
      case FormItemTypes.RADIO:
        return <FormRadioButtonGroup {...this.props} />;
      case FormItemTypes.AUTOCOMPLETE:
        return <FormAutocomplete {...this.props} />;
      case FormItemTypes.INPUT:
      default:
        return <FormInput {...this.props} />;
    }
  };

  renderLabeledItem = () => {
    return (
      <label className='FormItem__label' aria-label={this.props.ariaLabel}>
        <div className='FormItem__title'>{this.props.label}</div>
        <span className='a11y--visuallyHidden'>{this.props.description}</span>
        {this.renderItem()}
      </label>
    );
  };

  renderErrorMessage = () => {
    const { inputAttributes, formik } = this.props;
    return (
      FormUtils.hasMinSubmitCount(formik.submitCount) && (
        <ErrorMessage name={inputAttributes.name} className='FormItem__error' component='div' />
      )
    );
  };

  renderHint = () => {
    return <div className='FormItem__hint'>{this.props.hint}</div>;
  };

  render() {
    const { theme, inputAttributes, showsOn, formik } = this.props;
    if (inputAttributes.readonly) {
      inputAttributes.readonly = 'readonly';
    }
    if (showsOn && !FormUtils.getValues(formik.values, showsOn.name).match(showsOn.value)) {
      return null;
    }
    const formItemClasses = createBEM('FormItem', {
      [theme]: !!theme,
      error: FormUtils.shouldShowError(formik, inputAttributes.name),
    });
    return (
      <div className={formItemClasses}>
        {this.props.explanationControl && this.renderExplanationControl()}
        {this.props.label || this.props.ariaLabel ? this.renderLabeledItem() : this.renderItem()}
        {this.renderErrorMessage()}
        {this.props.hint && this.renderHint()}
      </div>
    );
  }
}

export default formikConnect(FormItem);
